/**
 * Article Creator Settings Service
 *
 * Handles company-level settings for the Article Creator tool.
 */

import { api } from './api';

// Settings types
export interface ArticleCreatorSettings {
  companyId: string;
  episodeTemplate?: string;
  clipTemplate?: string;
  outputFormat?: 'html' | 'markdown' | 'plain';
  createdAt: string;
  createdBy?: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface UpdateSettingsInput {
  episodeTemplate?: string;
  clipTemplate?: string;
  outputFormat?: ArticleCreatorSettings['outputFormat'];
}

export interface ClipPreviewData {
  articleTitle: string;
  articleDescription: string;
  articleUrl: string;
  articleImage: string;
}

export interface TemplatePreviewData {
  hasData: boolean;
  episodeTitle: string;
  episodeDescription: string;
  episodeAudioUrl: string;
  episodeArtwork: string;
  episodeDuration: string;
  podbeanEpisodeId: string;
  clips: ClipPreviewData[];
}

const SETTINGS_ENDPOINT = '/article-creator/settings';
const TEMPLATE_PREVIEW_ENDPOINT = '/article-creator/template-preview';

export const SettingsService = {
  /**
   * Get article creator settings for a company
   * @param companyId - Optional company ID. If not provided, uses current user's company.
   */
  async getSettings(companyId?: string): Promise<ArticleCreatorSettings> {
    const endpoint = companyId
      ? `${SETTINGS_ENDPOINT}?companyId=${encodeURIComponent(companyId)}`
      : SETTINGS_ENDPOINT;
    const response = await api.get<ArticleCreatorSettings>(endpoint);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch settings');
    }
    return response.data;
  },

  /**
   * Update article creator settings for a company
   * @param updates - Settings to update
   * @param companyId - Optional company ID. If not provided, uses current user's company.
   */
  async updateSettings(updates: UpdateSettingsInput, companyId?: string): Promise<ArticleCreatorSettings> {
    const endpoint = companyId
      ? `${SETTINGS_ENDPOINT}?companyId=${encodeURIComponent(companyId)}`
      : SETTINGS_ENDPOINT;
    const response = await api.put<ArticleCreatorSettings>(endpoint, updates);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to update settings');
    }
    return response.data;
  },

  /**
   * Get template preview data from most recent published episode
   * @param companyId - Optional company ID. If not provided, uses current user's company.
   */
  async getTemplatePreviewData(companyId?: string): Promise<TemplatePreviewData> {
    const endpoint = companyId
      ? `${TEMPLATE_PREVIEW_ENDPOINT}?companyId=${encodeURIComponent(companyId)}`
      : TEMPLATE_PREVIEW_ENDPOINT;
    const response = await api.get<TemplatePreviewData>(endpoint);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch preview data');
    }
    return response.data;
  },
};

export default SettingsService;
